import { useBlockProps, InspectorControls, BlockControls } from "@wordpress/block-editor";
import { PanelBody, SelectControl, TextControl, Button, Disabled, ToolbarGroup, ToolbarDropdownMenu } from "@wordpress/components";
import DebouncedServerSideRender from "../../../shared/js/DebouncedServerSideRender";
import { __ } from "@wordpress/i18n";

const VARIANT_OPTIONS = [
    { label: __("Default", "content-egg"), value: "default" },
    { label: __("Compact", "content-egg"), value: "compact" },
    { label: __("Grid", "content-egg"), value: "grid" },
    { label: __("Category Grid", "content-egg"), value: "category-grid" },
];

const COLOR_SCHEME_OPTIONS = [
    { label: __("Auto (site default)", "content-egg"), value: "auto" },
    { label: __("Light", "content-egg"), value: "light" },
    { label: __("Dark", "content-egg"), value: "dark" },
];

export default function Edit({ attributes, setAttributes }) {
    const blockProps = useBlockProps({ className: attributes.align ? `align${attributes.align}` : "" });
    const { variant, overall_score, overall_denom, stars, categories = [], color_scheme } = attributes;

    const updateCategories = (nextCategories) => setAttributes({ categories: nextCategories });
    const addCategory = () => updateCategories([...(categories || []), { label: "", score: "" }]);
    const updateCategory = (index, key, value) => {
        const next = [...(categories || [])];
        next[index] = {
            label: "",
            score: "",
            ...(next[index] || {}),
            [key]: value,
        };
        updateCategories(next);
    };
    const removeCategory = (index) => {
        const next = [...(categories || [])];
        next.splice(index, 1);
        updateCategories(next);
    };

    const hasPreviewContent =
        (variant !== "category-grid" && (overall_score || "").trim() !== "") ||
        (categories || []).some((item) => (item?.label || "").trim() !== "" || (item?.score || "").trim() !== "");

    return (
        <>
            <BlockControls>
                <ToolbarGroup>
                    <ToolbarDropdownMenu
                        icon="screenoptions"
                        label={__("Variant", "content-egg")}
                        text={VARIANT_OPTIONS.find((o) => o.value === variant)?.label}
                        controls={VARIANT_OPTIONS.map((option) => ({
                            title: option.label,
                            isActive: variant === option.value,
                            onClick: () => setAttributes({ variant: option.value }),
                        }))}
                    />
                </ToolbarGroup>
            </BlockControls>

            <InspectorControls>
                <PanelBody title={__("Layout", "content-egg")} initialOpen={true}>
                    <SelectControl
                        label={__("Variant", "content-egg")}
                        value={variant}
                        options={VARIANT_OPTIONS}
                        onChange={(value) => setAttributes({ variant: value })}
                        __nextHasNoMarginBottom
                    />
                    <SelectControl
                        label={__("Color Scheme", "content-egg")}
                        value={color_scheme}
                        options={COLOR_SCHEME_OPTIONS}
                        onChange={(value) => setAttributes({ color_scheme: value })}
                        __nextHasNoMarginBottom
                    />
                </PanelBody>

                <PanelBody title={__("Overall", "content-egg")} initialOpen={true}>
                    <TextControl
                        label={__("Overall Score", "content-egg")}
                        value={overall_score}
                        onChange={(value) => setAttributes({ overall_score: value })}
                    />
                    <TextControl
                        label={__("Overall Denominator", "content-egg")}
                        value={overall_denom}
                        onChange={(value) => setAttributes({ overall_denom: value })}
                    />
                    <TextControl
                        label={__("Stars", "content-egg")}
                        type="number"
                        min={0}
                        max={5}
                        step={0.5}
                        value={stars || ""}
                        onChange={(value) => setAttributes({ stars: value === "" ? 0 : Number(value) })}
                        help={__("Leave empty to derive stars from the overall score.", "content-egg")}
                        __nextHasNoMarginBottom
                    />
                </PanelBody>

                <PanelBody title={__("Categories", "content-egg")} initialOpen={false}>
                    {(categories || []).map((item, index) => (
                        <div
                            key={index}
                            style={{
                                marginBottom: index === categories.length - 1 ? "0" : "10px",
                                paddingBottom: index === categories.length - 1 ? "0" : "10px",
                                borderBottom: index === categories.length - 1 ? "none" : "1px solid #e0e0e0",
                            }}
                        >
                            <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: "6px" }}>
                                <Button
                                    icon="no-alt"
                                    isDestructive
                                    size="small"
                                    onClick={() => removeCategory(index)}
                                    label={__("Remove", "content-egg")}
                                />
                            </div>
                            <TextControl
                                label={__("Label", "content-egg")}
                                value={item?.label || ""}
                                onChange={(value) => updateCategory(index, "label", value)}
                            />
                            <TextControl
                                label={__("Score", "content-egg")}
                                value={item?.score || ""}
                                onChange={(value) => updateCategory(index, "score", value)}
                                __nextHasNoMarginBottom
                            />
                        </div>
                    ))}
                    <Button variant="secondary" size="small" onClick={addCategory} style={{ marginTop: "10px" }}>
                        {__("Add category", "content-egg")}
                    </Button>
                </PanelBody>
            </InspectorControls>

            <div {...blockProps}>
                {hasPreviewContent ? (
                    <Disabled>
                        <DebouncedServerSideRender
                            block="eggb/rating-breakdown"
                            attributes={attributes}
                        />
                    </Disabled>
                ) : (
                    <div
                        style={{
                            minHeight: "120px",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            padding: "20px",
                            border: "1px dashed #c3c4c7",
                            borderRadius: "6px",
                            background: "#f6f7f7",
                            textAlign: "center",
                        }}
                    >
                        <p style={{ color: "#757575", fontStyle: "italic", margin: 0, fontSize: "13px" }}>
                            {__("Add rating scores in the sidebar.", "content-egg")}
                        </p>
                    </div>
                )}
            </div>
        </>
    );
}
